import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { sprekersApi } from '../api';
import SharingControl from './SharingControl';
import type { RecordVisibility, SermonContent } from '../types';

interface FormState {
  title: string;
  core_message: string;
  content: string;
  visibility: RecordVisibility;
  shared_with_user_ids: string[];
}

const EMPTY_FORM: FormState = { title: '', core_message: '', content: '', visibility: 'prive', shared_with_user_ids: [] };

function toFormState(item: SermonContent): FormState {
  return {
    title: item.title || '',
    core_message: item.core_message || '',
    content: item.content || '',
    visibility: item.visibility,
    shared_with_user_ids: item.shared_with_user_ids,
  };
}

interface Props {
  engagementId: string;
  currentUserId: string;
  canEdit: boolean;
}

export default function ContentTab({ engagementId, currentUserId, canEdit }: Props) {
  const [items, setItems] = useState<SermonContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const fetchItems = async () => {
    try {
      const data = await sprekersApi.listContent(engagementId);
      setItems(data);
    } catch (error) {
      toast.error('Kon preekvoorbereiding niet laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engagementId]);

  const startEdit = (item: SermonContent) => {
    setForm(toFormState(item));
    setEditingId(item.id);
  };

  const startNew = () => {
    setForm(EMPTY_FORM);
    setEditingId('new');
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      if (editingId === 'new') {
        await sprekersApi.createContent(engagementId, form);
        toast.success('Preekvoorbereiding aangemaakt');
      } else if (editingId) {
        await sprekersApi.updateContent(editingId, form);
        toast.success('Preekvoorbereiding bijgewerkt');
      }
      setEditingId(null);
      fetchItems();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Opslaan mislukt');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <p className="text-sm text-slate-500">Laden...</p>;

  if (editingId) {
    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="content-title">Titel</Label>
          <Input
            id="content-title"
            value={form.title}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, title: e.target.value })}
          />
        </div>
        <div>
          <Label htmlFor="content-core-message">Kernboodschap</Label>
          <Input
            id="content-core-message"
            value={form.core_message}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, core_message: e.target.value })}
          />
        </div>
        <div>
          <Label htmlFor="content-body">Inhoud</Label>
          <Textarea
            id="content-body"
            value={form.content}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setForm({ ...form, content: e.target.value })}
            rows={10}
            placeholder="Vrije tekst, outline of losse punten -- geen vast schema verplicht."
          />
        </div>
        <SharingControl
          visibility={form.visibility}
          sharedWithUserIds={form.shared_with_user_ids}
          onChange={(visibility, shared_with_user_ids) => setForm({ ...form, visibility, shared_with_user_ids })}
        />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => setEditingId(null)}>Annuleren</Button>
          <Button type="submit" disabled={submitting}>Opslaan</Button>
        </div>
      </form>
    );
  }

  return (
    <div className="space-y-3">
      {items.length === 0 && <p className="text-sm text-slate-500">Nog geen preekvoorbereiding.</p>}
      {items.map((item) => (
        <div key={item.id} className="rounded-lg border border-slate-100 p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-medium text-slate-900">{item.title || 'Zonder titel'}</p>
                <Badge className={item.visibility === 'gedeeld' ? 'bg-teal-100 text-teal-700' : 'bg-slate-100 text-slate-600'}>
                  {item.visibility === 'gedeeld' ? 'Gedeeld' : 'Privé'}
                </Badge>
              </div>
              {item.core_message && <p className="mt-1 text-sm text-slate-600">{item.core_message}</p>}
              {item.content && <p className="mt-2 whitespace-pre-wrap text-sm text-slate-500">{item.content}</p>}
            </div>
            {item.author_id === currentUserId && (
              <Button variant="ghost" size="icon" onClick={() => startEdit(item)} aria-label="Bewerken">
                <Pencil className="h-4 w-4 text-slate-400" />
              </Button>
            )}
          </div>
        </div>
      ))}

      {canEdit && (
        <Button variant="outline" onClick={startNew}>
          <Plus className="mr-2 h-4 w-4" />
          Preekvoorbereiding toevoegen
        </Button>
      )}
    </div>
  );
}
